import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HttpMethod, LockService, PopupService } from '@lbmx/root-services';
import { AppSetting } from '@lbmx/types';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, exhaustMap, map, takeUntil, tap } from 'rxjs/operators';
import { apiUrls } from 'src/app/features/member-module/url-config';
import { ConfigProvider } from 'src/app/provider/config-provider';
import { IAnalyticsAuth } from '../analytics/analytics.service';

export interface IPortalTemplate {
  toAddress: string;
  subject: string;
  emailText: string;
}

export interface IPortalAuth {
  loginURL: string;
  redirectURL: string;
  errorMessage: string;
}

@Injectable({
  providedIn: 'root',
})
export class NavBarService implements OnDestroy {
  private _shouldDestroy = new Subject();
  private showMarketplaceModalBehaviorSubject = new BehaviorSubject<number>(0);
  public showMarketplaceModal =
    this.showMarketplaceModalBehaviorSubject.asObservable();
  private showAccountModalBehaviorSubject = new BehaviorSubject<number>(0);
  public showAccountModal = this.showAccountModalBehaviorSubject.asObservable();
  private showPortalModalBehaviorSubject = new BehaviorSubject<number>(0);
  public showPortalModal = this.showPortalModalBehaviorSubject.asObservable();
  public appSettings: AppSetting = this.configPrv.AppSetting;

  constructor(
    private configPrv: ConfigProvider,
    private router: Router,
    private httpClient: HttpClient,
    private lockService: LockService,
    private popupService: PopupService
  ) {}

  public getPortalTemplate(): Observable<IPortalTemplate> {
    return this.httpClient
      .get(
        `${this.appSettings.uriConfiguration.configurationRootURL}/navbar/getPortalTemplate`,
        { withCredentials: true }
      )
      .pipe(
        map((res: any) => res.record),
        catchError((error) => of(error))
      );
  }

  public getLoginUrls(): Observable<any> {
    return this.httpClient
      .get(this.appSettings.uriSecurity.analytics, { withCredentials: true })
      .pipe(
        exhaustMap((result: IAnalyticsAuth) => {
          return this.httpClient
            .get(result.loginURL, {
              withCredentials: true,
              responseType: 'text',
              headers: new HttpHeaders({
                'Content-type': 'text/html; charset=utf-8',
              }),
            })
            .pipe(
              map(() => result.redirectURL),
              catchError((error) => of(error))
            );
        })
      );
  }

  public getPortalUrls(args: string[]): Observable<any> {
    return this.httpClient.get(this.appSettings.uriSecurity.portal, {
      withCredentials: true,
      params: { site: args[0], toolKey: args[1] },
    });
  }

  public runCommand(command: any) {
    if (command) {
      const key: string = Object.keys(command)[0];
      this.dispatcher(key, command[key]);
    }
  }

  public dispatcher(key: string, args: string[]) {
    switch (key) {
      case 'navigate':
        this.router.navigate(args);
        break;
      case 'switchMarketplace':
        this.switchMarketplace();
        break;
      case 'switchAccount':
        this.switchAccount();
        break;
      case 'groupSecurityToken':
        this.openGroupSecurityToken();
        break;
      case 'openAnalytics':
        this.openAnalytics();
        break;
      case 'openPortal':
        this.openPortal(args);
        break;
      case 'navigateExternal':
        this.navigateExternal(args);
        break;
      case 'demandPlanning':
        this.navigateExternal([
          this.configPrv.AppSetting.uriDemandPlanning.baseUrl.replace(
            /^(https?:\/\/)?(?:[^./]+\.)?([^/]+\.[^./]{2,})(?::\d+)?(?=\/|$)/,
            `$1demandplanning.$2`
          ),
        ]);
        break;
      default:
        break;
    }
  }

  public switchMarketplace() {
    this.lockService
      .acquire('switchMarketplace')
      ?.then(() => {
        this.showMarketplaceModalBehaviorSubject.next(Date.now());
      })
      .release();
  }

  public switchAccount() {
    this.lockService
      .acquire('switchMarketplace')
      ?.then(() => {
        this.showAccountModalBehaviorSubject.next(Date.now());
      })
      .release();
  }

  public openGroupSecurityToken() {
    this.popupService.openWizard({
      endpoint: apiUrls.generateWizardUrl(),
      httpMethod: HttpMethod.GET,
    });
  }

  private openAnalytics() {
    this.getLoginUrls()
      .pipe(
        tap((result: string) => {
          if (result.match(/^http/)) {
            window.open(result);
          }
        }),
        takeUntil(this._shouldDestroy)
      )
      .subscribe();
  }

  private openPortal(args: string[]) {
    this.getPortalUrls(args)
      .pipe(
        tap((result: IPortalAuth) => {
          if (result.errorMessage) {
            of(this.showPortalError());
          } else if (result.loginURL.match(/^http/)) {
            window.open(result.loginURL);
          }
        }),
        catchError(() => of(this.showPortalError())),
        takeUntil(this._shouldDestroy)
      )
      .subscribe();
  }

  private navigateExternal([url]: string[]) {
    window.location.href = url;
  }

  public ngOnDestroy(): void {
    this._shouldDestroy.next(true);
  }

  public showPortalError() {
    this.showPortalModalBehaviorSubject.next(Date.now());
  }
}
